/**
 * JobStatus Repository
 * 
 * Handles background job monitoring and status operations with GraphQL backend
 */

import { BaseRepository } from './BaseRepository';

export interface JobStatus {
    id: string;
    jobType: string;
    name: string;
    description?: string;
    status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'retrying';
    progress: number; // 0-100
    queuedAt: string;
    startedAt?: string;
    completedAt?: string;
    duration?: number;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    retryCount: number;
    maxRetries: number;
    retryBackoff?: number;
    lastError?: string;
    errorDetails?: Record<string, any>;
    resourceUsage?: ResourceUsage;
    parameters?: Record<string, any>;
    result?: Record<string, any>;
    logs: JobLog[];
    metadata?: Record<string, any>;
}

export interface ResourceUsage {
    cpuPercent: number;
    memoryUsage: number;
    memoryLimit: number;
    diskUsage?: number;
    networkIO?: {
        bytesIn: number;
        bytesOut: number;
    };
}

export interface JobLog {
    id: string;
    jobId: string;
    timestamp: string;
    level: 'debug' | 'info' | 'warn' | 'error';
    message: string;
    context?: Record<string, any>;
}

export interface JobMetrics {
    totalJobs: number;
    queuedJobs: number;
    runningJobs: number;
    completedJobs: number;
    failedJobs: number;
    avgExecutionTime: number;
    successRate: number;
    queueDepth: number;
    avgResourceUsage: {
        cpu: number;
        memory: number;
    };
}

class JobStatusRepository extends BaseRepository<JobStatus> {
    protected entityName = 'JobStatus';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<JobStatus | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'JobStatus',
            criticalityLevel: 'MEDIUM', // Job status monitoring is important but not critical
            metadata: { id, jobStatusRepository: true }
        });

        const query = `
            query GetJobStatusByTenantAndId($id: String!, $tenant: String!) {
                jobStatus(id: $id, tenant: $tenant) {
                    id
                    jobType
                    name
                    description
                    status
                    progress
                    queuedAt
                    startedAt
                    completedAt
                    duration
                    priority
                    retryCount
                    maxRetries
                    retryBackoff
                    lastError
                    errorDetails
                    resourceUsage {
                        cpuPercent
                        memoryUsage
                        memoryLimit
                        diskUsage
                        networkIO {
                            bytesIn
                            bytesOut
                        }
                    }
                    parameters
                    result
                    logs {
                        id
                        jobId
                        timestamp
                        level
                        message
                        context
                    }
                    metadata
                }
            }
        `;

        try {
            const result = await this.query<{ jobStatus: JobStatus | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 15 * 1000 // 15 seconds cache for job status (needs to be fresh)
            });

            const jobStatus = result.jobStatus || null;

            // ✅ FERP SAFETY: Log successful operation with job details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'JobStatus',
                    entityId: id,
                    found: !!jobStatus,
                    jobType: jobStatus?.jobType,
                    jobStatus: jobStatus?.status,
                    jobProgress: jobStatus?.progress,
                    timestamp: Date.now()
                });
            }

            return jobStatus;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'JobStatus',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(jobStatusId: string): Promise<JobStatus | null> {
        return this.findByTenantAndId(this.tenant, jobStatusId);
    }

    // Find all jobs with optional filtering
    async findAll(options?: {
        status?: string;
        jobType?: string;
        priority?: string;
        limit?: number;
        offset?: number;
    }): Promise<JobStatus[]> {
        const query = `
            query ListJobs(
                $status: JobStatus
                $jobType: String
                $priority: Priority
                $limit: Int
                $offset: Int
                $tenant: String!
            ) {
                jobs(
                    status: $status
                    jobType: $jobType
                    priority: $priority
                    limit: $limit
                    offset: $offset
                    tenant: $tenant
                ) {
                    id
                    jobType
                    name
                    description
                    status
                    progress
                    queuedAt
                    startedAt
                    completedAt
                    duration
                    priority
                    retryCount
                    maxRetries
                    retryBackoff
                    lastError
                    errorDetails
                    resourceUsage {
                        cpuPercent
                        memoryUsage
                        memoryLimit
                        diskUsage
                        networkIO {
                            bytesIn
                            bytesOut
                        }
                    }
                    parameters
                    result
                    metadata
                }
            }
        `;

        const variables = {
            status: options?.status,
            jobType: options?.jobType,
            priority: options?.priority,
            limit: options?.limit,
            offset: options?.offset
        };
        const result = await this.query<{ jobs: JobStatus[] }>(query, variables);
        return result.jobs || [];
    }

    // Find job by ID with full details including logs
    async findOne(jobId: string): Promise<JobStatus | null> {
        const query = `
            query GetJob($id: String!, $tenant: String!) {
                job(id: $id, tenant: $tenant) {
                    id
                    jobType
                    name
                    description
                    status
                    progress
                    queuedAt
                    startedAt
                    completedAt
                    duration
                    priority
                    retryCount
                    maxRetries
                    retryBackoff
                    lastError
                    errorDetails
                    resourceUsage {
                        cpuPercent
                        memoryUsage
                        memoryLimit
                        diskUsage
                        networkIO {
                            bytesIn
                            bytesOut
                        }
                    }
                    parameters
                    result
                    logs {
                        id
                        jobId
                        timestamp
                        level
                        message
                        context
                    }
                    metadata
                }
            }
        `;

        const result = await this.query<{ job: JobStatus | null }>(query, { id: jobId });
        return result.job || null;
    }

    // Get job logs
    async getLogs(jobId: string, options?: {
        level?: string;
        startTime?: string;
        endTime?: string;
        limit?: number;
    }): Promise<JobLog[]> {
        const query = `
            query GetJobLogs(
                $jobId: String!
                $level: LogLevel
                $startTime: String
                $endTime: String
                $limit: Int
                $tenant: String!
            ) {
                jobLogs(
                    jobId: $jobId
                    level: $level
                    startTime: $startTime
                    endTime: $endTime
                    limit: $limit
                    tenant: $tenant
                ) {
                    id
                    jobId
                    timestamp
                    level
                    message
                    context
                }
            }
        `;

        const result = await this.query<{ jobLogs: JobLog[] }>(query, { jobId, ...options });
        return result.jobLogs || [];
    }

    // Create new job
    async create(jobData: {
        jobType: string;
        name: string;
        description?: string;
        priority?: string;
        maxRetries?: number;
        retryBackoff?: number;
        parameters?: Record<string, any>;
    }): Promise<JobStatus> {
        const mutation = `
            mutation CreateJob($input: CreateJobInput!, $tenant: String!) {
                createJob(input: $input, tenant: $tenant) {
                    id
                    jobType
                    name
                    description
                    status
                    progress
                    queuedAt
                    priority
                    retryCount
                    maxRetries
                    retryBackoff
                    parameters
                    metadata
                }
            }
        `;

        const result = await this.mutation<{ createJob: JobStatus }>(mutation, { input: jobData });
        return result.createJob;
    }

    // Update job progress
    async updateProgress(jobId: string, progress: number, message?: string): Promise<JobStatus> {
        const mutation = `
            mutation UpdateJobProgress($id: String!, $progress: Int!, $message: String, $tenant: String!) {
                updateJobProgress(id: $id, progress: $progress, message: $message, tenant: $tenant) {
                    id
                    progress
                    status
                    lastError
                }
            }
        `;

        const result = await this.mutation<{ updateJobProgress: JobStatus }>(mutation, {
            id: jobId,
            progress,
            message
        });
        return result.updateJobProgress;
    }

    // Cancel job
    async cancel(jobId: string, reason?: string): Promise<JobStatus> {
        const mutation = `
            mutation CancelJob($id: String!, $reason: String, $tenant: String!) {
                cancelJob(id: $id, reason: $reason, tenant: $tenant) {
                    id
                    status
                    completedAt
                    lastError
                }
            }
        `;

        const result = await this.mutation<{ cancelJob: JobStatus }>(mutation, {
            id: jobId,
            reason
        });
        return result.cancelJob;
    }

    // Retry failed job
    async retry(jobId: string): Promise<JobStatus> {
        const mutation = `
            mutation RetryJob($id: String!, $tenant: String!) {
                retryJob(id: $id, tenant: $tenant) {
                    id
                    status
                    retryCount
                    queuedAt
                    lastError
                }
            }
        `;

        const result = await this.mutation<{ retryJob: JobStatus }>(mutation, { id: jobId });
        return result.retryJob;
    }

    // Delete completed job
    async delete(jobId: string): Promise<boolean> {
        const mutation = `
            mutation DeleteJob($id: String!, $tenant: String!) {
                deleteJob(id: $id, tenant: $tenant)
            }
        `;

        const result = await this.mutation<{ deleteJob: boolean }>(mutation, { id: jobId });
        return result.deleteJob || false;
    }

    // Clean up completed jobs
    async cleanup(options: {
        olderThanDays: number;
        statusFilter?: string[];
        jobTypeFilter?: string[];
    }): Promise<number> {
        const mutation = `
            mutation CleanupJobs($input: CleanupJobsInput!, $tenant: String!) {
                cleanupJobs(input: $input, tenant: $tenant) {
                    deletedCount
                }
            }
        `;

        const result = await this.mutation<{ cleanupJobs: { deletedCount: number } }>(mutation, { input: options });
        return result.cleanupJobs?.deletedCount || 0;
    }

    // Get job metrics
    async getMetrics(options?: {
        jobType?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<JobMetrics> {
        const query = `
            query GetJobMetrics(
                $jobType: String
                $startDate: String
                $endDate: String
                $tenant: String!
            ) {
                jobMetrics(
                    jobType: $jobType
                    startDate: $startDate
                    endDate: $endDate
                    tenant: $tenant
                ) {
                    totalJobs
                    queuedJobs
                    runningJobs
                    completedJobs
                    failedJobs
                    avgExecutionTime
                    successRate
                    queueDepth
                    avgResourceUsage {
                        cpu
                        memory
                    }
                }
            }
        `;

        const result = await this.query<{ jobMetrics: JobMetrics }>(query, options);
        return result.jobMetrics || {
            totalJobs: 0,
            queuedJobs: 0,
            runningJobs: 0,
            completedJobs: 0,
            failedJobs: 0,
            avgExecutionTime: 0,
            successRate: 0,
            queueDepth: 0,
            avgResourceUsage: { cpu: 0, memory: 0 }
        };
    }

    // Get job queue status
    async getQueueStatus(): Promise<{
        totalQueued: number;
        averageWaitTime: number;
        processingCapacity: number;
        estimatedProcessingTime: number;
    }> {
        const query = `
            query GetJobQueueStatus($tenant: String!) {
                jobQueueStatus(tenant: $tenant) {
                    totalQueued
                    averageWaitTime
                    processingCapacity
                    estimatedProcessingTime
                }
            }
        `;

        const result = await this.query(query, 'GetJobQueueStatus', {});
        return result.jobQueueStatus || {
            totalQueued: 0,
            averageWaitTime: 0,
            processingCapacity: 0,
            estimatedProcessingTime: 0
        };
    }
}

// Export singleton instance
export const jobStatusRepository = new JobStatusRepository();
export { JobStatusRepository };
